import type { CSSProperties, ReactNode } from 'react'

import { MuscleFigure } from '../../muscle/MuscleFigure'
import { TOKENS } from '../../ui/dashboard-primitives'
import { ArtifactCard } from './ArtifactCard'
import type { MusclesArtifactData } from './types'

// Read from MuscleFigure's own FILL_PRIM / FILL_SEC. A legend that does not
// match what the figure paints is worse than no legend at all.
const SWATCH_PRIMARY = TOKENS.amber
const SWATCH_SECONDARY = TOKENS.amberEmphasis

const LEGEND_TEXT: CSSProperties = {
  fontSize: 12,
  color: TOKENS.creamSoft,
}

function LegendRow({ swatch, children }: { swatch: string; children: ReactNode }) {
  return (
    <div className="grid items-baseline gap-2.5" style={{ gridTemplateColumns: 'auto 1fr' }}>
      <span
        aria-hidden
        style={{ width: 10, height: 10, borderRadius: 3, background: swatch, alignSelf: 'center' }}
      />
      <span style={LEGEND_TEXT}>{children}</span>
    </div>
  )
}

/**
 * `muscles` — the body-map highlight: what one movement loads, prime movers
 * distinguished from assisting muscles.
 *
 * The figure gets `highlight_*`, already folded server-side through
 * `muscle_map.highlight_slugs`. MuscleFigure applies the same alias itself, so
 * passing folded slugs is idempotent. The legend uses the unfolded labels,
 * which stay anatomically correct.
 *
 * Risk W1 — the muscle polygon data exists twice (here and
 * `BodySilhouette.swift`), hand-synced, with no shared source between the
 * toolchains, and `MuscleFigure` silently ignores a slug it cannot draw. So a
 * slug added to `api/muscle_map.py` alone would paint nothing, with no error.
 * Checked 2026-08-07: all 17 slugs the backend can emit after
 * `highlight_slugs()` are drawable. The test
 * "can draw every muscle slug the backend is able to emit" is what keeps that
 * true — it is the only thing enforcing the sync on this side.
 *
 * The legend deliberately renders off `primary`/`secondary` — the training
 * vocabulary — rather than off `highlight_*`, so its labels stay anatomically
 * precise (a side delt reads "Side delts" even though the figure folds it onto
 * the front deltoid), and so the card still names what it loaded if a future
 * slug ever does go undrawn.
 */
export function MusclesArtifactCard({ data }: { data: MusclesArtifactData }) {
  return (
    <ArtifactCard eyebrow="Muscles loaded" meta="Primary · Secondary" cardType="muscles">
      <div className="flex items-start" style={{ gap: 24 }}>
        <div style={{ width: 84 }}>
          <MuscleFigure
            side="front"
            primary={data.highlight_primary}
            secondary={data.highlight_secondary}
            className="w-full h-auto"
          />
        </div>
        <div style={{ width: 84 }}>
          <MuscleFigure
            side="back"
            primary={data.highlight_primary}
            secondary={data.highlight_secondary}
            className="w-full h-auto"
          />
        </div>
        <div className="flex flex-1 flex-col gap-2" style={{ paddingTop: 4 }}>
          {data.primary.length > 0 ? (
            <LegendRow swatch={SWATCH_PRIMARY}>
              {data.primary.map((entry) => entry.label).join(', ')}{' '}
              ({data.primary.length === 1 ? 'prime mover' : 'prime movers'})
            </LegendRow>
          ) : null}
          {data.secondary.length > 0 ? (
            <LegendRow swatch={SWATCH_SECONDARY}>
              {data.secondary.map((entry) => entry.label).join(', ')} (assisting)
            </LegendRow>
          ) : null}
        </div>
      </div>
    </ArtifactCard>
  )
}
